import React, { createContext, useContext, useMemo, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getDomainById, type DomainConfig } from '@/domain-registry';
import { useDomainFromUrl } from '@/shared/hooks/useDomainFromUrl';
import { useDomainSwitcher } from '@/shared/hooks/useDomainSwitcher';
import { useAccessibleDomains } from '@/shared/hooks/useAvailableDomains';

// Domain context interface
interface DomainContextType {
  currentDomain: string;
  setCurrentDomain: (domainId: string) => void;
  domainConfig: DomainConfig | null;
  availableDomains: DomainConfig[];
  switchToDomain: (domainId: string, preservePath?: boolean) => void;
}

// Create the context
const DomainContext = createContext<DomainContextType | null>(null);

// Domain provider props
interface DomainProviderProps {
  children: React.ReactNode;
}

/**
 * Domain Provider
 * URL is the single source of truth for domain state
 * All domain values are derived from the URL with no internal state
 */
export function DomainProvider({ children }: DomainProviderProps) {
  const navigate = useNavigate();
  
  // Get domain directly from URL
  const { currentDomain, domainConfig } = useDomainFromUrl();
  
  // Get all accessible domains for the domain switcher (includes hidden domains like Admin)
  const availableDomains = useAccessibleDomains();
  
  // Get domain switching function
  const { switchToDomain } = useDomainSwitcher();
  
  // Simplified setCurrentDomain for API compatibility
  const setCurrentDomain = useCallback((domainId: string) => {
    const domain = getDomainById(domainId);
    if (domain) {
      navigate(domain.defaultRoute, { replace: true });
    } else {
      console.warn(`[DomainProvider] Domain not found: ${domainId}`);
    }
  }, [navigate]);
  
  // Context value - all derived from URL
  const contextValue = useMemo<DomainContextType>(() => ({
    currentDomain,
    setCurrentDomain,
    domainConfig,
    availableDomains,
    switchToDomain,
  }), [currentDomain, setCurrentDomain, domainConfig, availableDomains, switchToDomain]);

  return (
    <DomainContext.Provider value={contextValue}>
      {children}
    </DomainContext.Provider>
  );
}

/**
 * Hook to use domain context
 * During HMR (Hot Module Replacement), this may be called before provider is ready
 * In development, we provide a temporary fallback to prevent crashes during hot reloads
 */
export function useDomain(): DomainContextType {
  const context = useContext(DomainContext);
  const location = useLocation();
  
  if (!context) {
    // In development mode during HMR, provide a temporary fallback
    if (import.meta.env.DEV) {
      console.warn('[useDomain] Context not available - likely due to HMR. Using temporary fallback.');
      
      // Try to extract domain from current URL as fallback
      const pathSegments = location.pathname.split('/').filter(Boolean);
      const potentialDomain = pathSegments[0] || 'billing';
      const fallbackConfig = getDomainById(potentialDomain) || getDomainById('billing') || null;
      
      // Return a temporary fallback context with URL-derived domain
      return {
        currentDomain: fallbackConfig?.id || 'billing',
        setCurrentDomain: () => {
          console.warn('[useDomain] setCurrentDomain called during HMR fallback - ignoring');
        },
        domainConfig: fallbackConfig,
        availableDomains: [],
        switchToDomain: () => {
          console.warn('[useDomain] switchToDomain called during HMR fallback - ignoring');
        },
      };
    }
    throw new Error('useDomain must be used within a DomainProvider');
  }
  
  return context;
}

// Export context for external use
export { DomainContext };